import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { DataTableV3 } from "../../components/DataTable/DataTableV3";
import {
    ApiValidationError,
    createDefinition,
    fetchDefinitions,
} from "../../components/WorkflowEditor/api";
import type { WorkflowDefinitionRow } from "../../components/WorkflowEditor/types";
import { useAdminApiClient, useAppSelector } from "../../hooks";
import { PrimaryButton, SecondaryButton } from "../../ui/Buttons";
import { Card } from "../../ui/Card";
import { FilteringDropdown } from "../../ui/Inputs";
import { Modal } from "../../ui/Modals";
import { ROUTES } from "..";

/** The workflow definitions list — the editor's entry point, and where one is created. */

const TABLE_NAME = "workflow-definitions";

const columnHelper = createColumnHelper<WorkflowDefinitionRow>();

const columns = [
    columnHelper.accessor("name", {
        id: "name",
        header: () => "Name",
        cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("ownerName", {
        id: "ownerName",
        header: () => "Company",
        // No company means a global template, which is a real state rather than missing data.
        cell: (info) => info.getValue() ?? "Global template",
    }),
    columnHelper.accessor("isActive", {
        id: "isActive",
        header: () => "Active",
        cell: (info) => (info.getValue() ? "Yes" : "No"),
    }),
    columnHelper.accessor("transitionCount", {
        id: "transitionCount",
        header: () => "Transitions",
        // Zero transitions means nothing at all happens to an event under this definition.
        cell: (info) =>
            info.getValue() === 0 ? "none — does nothing yet" : info.getValue(),
    }),
    columnHelper.accessor("actionCount", {
        id: "actionCount",
        header: () => "Actions",
        cell: (info) => info.getValue(),
    }),
];

function CreateDefinitionModal({
    visible,
    onClose,
}: {
    visible: boolean;
    onClose: () => void;
}) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const adminApiClient = useAdminApiClient();

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [owner, setOwner] = useState<number | null>(null);
    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const [saving, setSaving] = useState(false);

    const companiesQuery = useQuery({
        queryKey: ["staffCompanyList"],
        queryFn: () => adminApiClient.adminAccountsCompaniesList(),
        refetchOnWindowFocus: false,
    });

    const submit = async () => {
        setErrors({});
        setSaving(true);
        try {
            const created = await createDefinition({
                name,
                description,
                owner,
            });
            await queryClient.refetchQueries({ queryKey: [TABLE_NAME] });
            // Straight into the editor: a new definition has no transitions, so adding one is
            // the only useful next step.
            navigate(`${ROUTES.ADMIN_WORKFLOWS}/${created.id}`);
        } catch (error) {
            if (error instanceof ApiValidationError) {
                // The server rejects a company that already has a definition, which is the
                // mistake this form exists to make survivable.
                setErrors(error.fieldErrors);
            } else {
                setErrors({
                    detail: [
                        error instanceof Error ? error.message : String(error),
                    ],
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldError = (field: string) =>
        errors[field] ? (
            <p className="mt-1 text-sm text-red-600">
                {errors[field].join(" ")}
            </p>
        ) : null;

    return (
        <Modal visible={visible} onClose={onClose} size="sm">
            <div className="p-2">
                <h2 className="text-lg font-medium">New workflow definition</h2>
                <p className="mt-1 text-sm text-gray-500">
                    A company can have at most one definition. Leave the company
                    empty to create a global template.
                </p>

                <div className="mt-4">
                    <label className="block text-sm" htmlFor="wf-name">
                        Name
                    </label>
                    <input
                        id="wf-name"
                        className="mt-1 w-full rounded"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                    />
                    {fieldError("name")}
                </div>

                <div className="mt-4">
                    <label className="block text-sm" htmlFor="wf-description">
                        Description
                    </label>
                    <textarea
                        id="wf-description"
                        className="mt-1 w-full rounded"
                        rows={2}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                    {fieldError("description")}
                </div>

                <div className="mt-4">
                    {/* biome-ignore lint/a11y/noLabelWithoutControl: FilteringDropdown renders
                        its own combobox input, which this labels. */}
                    <label className="block text-sm">Company</label>
                    {companiesQuery.data ? (
                        <FilteringDropdown
                            value={owner ?? undefined}
                            onChange={(value) =>
                                setOwner(value === null ? null : Number(value))
                            }
                            options={companiesQuery.data.map((company) => ({
                                id: company.id,
                                displayName: company.name,
                                searchString: company.name.toLowerCase(),
                            }))}
                        />
                    ) : (
                        <p className="mt-1 text-sm text-gray-500">
                            Loading companies…
                        </p>
                    )}
                    {fieldError("owner")}
                </div>

                {fieldError("detail")}

                <div className="mt-6 flex justify-end gap-2">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                    <PrimaryButton onClick={submit} disabled={!name || saving}>
                        {saving ? "Creating…" : "Create"}
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
}

export function AdminWorkflowsPage() {
    const isSuperuser = useAppSelector((state) => state.auth.isSuperuser);
    const navigate = useNavigate();
    const [showCreate, setShowCreate] = useState(false);

    // Gated on superuser, not staff. The editor API is IsAdminSuperUser and its viewsets do not
    // scope querysets by company, so a staff-only gate here would render a page whose every
    // request 403s.
    if (!isSuperuser) {
        return <Navigate to={ROUTES.ROOT} />;
    }

    return (
        <div className="container mx-auto px-6">
            <h1 className="mt-6 text-2xl">Workflow definitions</h1>
            <Card>
                <DataTableV3<WorkflowDefinitionRow>
                    dataName={TABLE_NAME}
                    columns={columns}
                    fetchFunction={fetchDefinitions}
                    sortable={false}
                    filterable={false}
                    extraFilters={{}}
                    createRow={() => setShowCreate(true)}
                    onClickRow={(id: string) =>
                        navigate(`${ROUTES.ADMIN_WORKFLOWS}/${id}`)
                    }
                />
            </Card>

            <CreateDefinitionModal
                visible={showCreate}
                onClose={() => setShowCreate(false)}
            />
        </div>
    );
}
